import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { StoreKitProduct } from "../services/RevenueCatService";
import { useAppSettings } from "../components/providers/AppSettingsProvider";
import { getPageBackgroundGradient } from "../config/theme";

const PurchaseSuccess: React.FC = () => {
  const { t, theme } = useAppSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme === 'dark';
  
  // Get product info from navigation state
  const product = (location.state as { product?: StoreKitProduct })?.product;

  const tierKey = (() => {
    const pkg = (product?.rcPackageIdentifier || "").toLowerCase();
    if (pkg.includes("platinum")) return "platinum";
    return "pro";
  })();

  const isPlatinum = tierKey === "platinum";
  const isPro = tierKey === "pro";

  // Get success background image based on tier and theme
  const successImage = isPlatinum
    ? (isDark ? "/images/platinum_success_dark.png" : "/images/platinum_success_light.png")
    : (isDark ? "/images/pro_success_dark.png" : "/images/pro_success_light.png");

  // Get features text based on tier
  const featuresText = isPlatinum
    ? t("purchaseSuccess.features.platinum")
    : isPro
      ? t("purchaseSuccess.features.pro")
      : "";


  // Text colors
  const headerTextColor = isDark ? "text-white" : "text-gray-800";
  const contentTextColor = isDark ? "text-white" : "text-gray-800";
  const featureTextColor = isDark ? "text-gray-200" : "text-gray-600";

  // Button colors
  const buttonBg = isDark
    ? "bg-gradient-to-r from-[#FFE066] to-[#FFD86B]"
    : "bg-gradient-to-r from-[#FFF0B3] to-[#FFE8A0]";
  const buttonTextColor = isDark ? "text-[#0B3CBA]" : "text-gray-800";

  return (
    <div className="fixed inset-0 w-full h-screen overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />
      
      {/* Back Button - Top Left */}
      <div className="fixed top-16 left-6 z-50">
        <button
          onClick={() => navigate("/profile")}
          className={`p-2 transition-colors`}
        >
          <ArrowLeft className={`w-6 h-6 ${isDark ? "text-white" : "text-gray-800"}`} strokeWidth={2.5} />
        </button>
      </div>
      
      <div className="relative z-10">
        {/* Upper Section - Header with success image */}
        <div className="relative flex flex-col items-center pt-[8vh] pb-8">
          {/* Title - placed on top */}
          <div className="w-full text-center mb-4 px-6">
            <h1 className={`text-2xl md:text-3xl font-bold ${headerTextColor}`}>
              {isPlatinum
                ? t("purchaseSuccess.title.platinum")
                : isPro
                  ? t("purchaseSuccess.title.pro")
                  : t("purchaseSuccess.title")}
            </h1>
          </div>

          {/* Success image - full width, no exceed */}
          <div className="w-full px-0 flex items-center justify-center">
            <img
              src={successImage}
              alt="Success"
              className="w-full h-auto object-contain max-h-[40vh]"
            />
          </div>
        </div>

        {/* Lower Section - Content */}
        <div className="relative flex flex-col items-center justify-center px-6 py-8">
          <div className="w-full max-w-md space-y-6">
            {/* Main Heading */}
            <h2 className={`text-2xl md:text-3xl font-bold text-left ${contentTextColor}`}>
              {isPlatinum
                ? t("purchaseSuccess.subtitle.platinum")
                : isPro
                  ? t("purchaseSuccess.subtitle.pro")
                  : t("purchaseSuccess.subtitle.generic")}
            </h2>

            {/* Features Text */}
            <div className="space-y-4">
              {featuresText.split('\n').map((line, index) => (
                <p key={index} className={`text-base md:text-lg text-left font-semibold ${featureTextColor}`}>
                  {line}
                </p>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <button
                onClick={() => navigate("/whisper")}
                className={`w-full rounded-2xl ${buttonBg} ${buttonTextColor} py-4 text-lg font-bold shadow-lg transition-transform active:scale-95`}
              >
                {t("purchaseSuccess.getStarted")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSuccess;
